import { Module } from 'vuex';
import * as firebase from 'firebase/app';
import 'firebase/firestore';
import { serializer } from '@/api/shared/serializer';
import { UserTypeEnum } from '@/enums/UserTypeEnum';
import {
  FamilyFeedback,
  FamilyFeedbackRequest,
  FeedbackComment,
  FeedbackMessage,
  normalizeFeedbackType
} from '@/models/FeedbackModels';
import moment from 'moment';
import { localizedDate } from '@/utils/localizedDate';

const feedbackModule: Module<any, any> = {
  namespaced: false,
  state: {
    familyFeedbackRequests: [] as FamilyFeedbackRequest[],
    currentFeedbackRequest: null as FamilyFeedbackRequest | null,
    feedbackMessages: [] as FeedbackMessage[],
    loadingFeedbackRequests: false,
    loadingCurrentRequest: false,
    loadingMessages: false,
  },

  mutations: {
    setFamilyFeedbackRequests(state, payload: FamilyFeedbackRequest[]) {
      state.familyFeedbackRequests = payload;
    },
    setCurrentFeedbackRequest(state, payload: FamilyFeedbackRequest | null) {
      state.currentFeedbackRequest = payload;
    },
    setLoadingFeedbackRequests(state, payload: boolean) {
      state.loadingFeedbackRequests = payload;
    },
    setLoadingCurrentRequest(state, payload: boolean) {
      state.loadingCurrentRequest = payload;
    },
    setFeedbackMessages(state, payload: FeedbackMessage[]) {
      state.feedbackMessages = payload;
    },
    addFeedbackMessage(state, message: FeedbackMessage) {
      state.feedbackMessages.push(message);
    },
    setLoadingMessages(state, payload: boolean) {
      state.loadingMessages = payload;
    },
    addCommentToCurrentRequest(state, comment: FeedbackComment) {
      if (state.currentFeedbackRequest) {
        if (!state.currentFeedbackRequest.comments) {
          state.currentFeedbackRequest.comments = [];
        }
        state.currentFeedbackRequest.comments.push(comment);
      }
    },
    updateFeedbackRequestStatus(state, { requestId, status }: { requestId: string; status: string }) {
      const request = state.familyFeedbackRequests.find((r: FamilyFeedbackRequest) => r.id === requestId);
      if (request) {
        request.status = status;
      }
      if (state.currentFeedbackRequest?.id === requestId) {
        state.currentFeedbackRequest.status = status;
      }
    }
  },

  actions: {
    async getFamilyFeedbackRequests({ commit }) {
      commit('setLoadingFeedbackRequests', true);
      try {
        const facilityId = this.state.authModule.currentFacility;
        
        console.log('Fetching feedback requests for facilityId:', facilityId);
        console.log('Current user:', this.state.authModule.user);
        
        // Collection name: familyFeedback (confirmed by Vitalik from Stas)
        const snapshot = await firebase
          .firestore()
          .collection('familyFeedback')
          .where('facilityId', '==', facilityId)
          .orderBy('createdAt', 'desc')
          .get();

        const requests: FamilyFeedbackRequest[] = serializer.querySnapshotToDocuments(snapshot);
        requests.forEach(r => { r.type = normalizeFeedbackType(r.type); });
        console.log('Successfully fetched feedback requests:', requests.length);
        commit('setFamilyFeedbackRequests', requests);
      } catch (error) {
        console.error('Error fetching family feedback requests:', error);
        if (error && typeof error === 'object') {
          console.error('Error code:', (error as {code?: string}).code);
          console.error('Error message:', (error as {message?: string}).message);
        }
        throw error;
      } finally {
        commit('setLoadingFeedbackRequests', false);
      }
    },

    async getFeedbackRequestById({ commit }, requestId: string) {
      commit('setLoadingCurrentRequest', true);
      try {
        // Query Firestore for the feedback request
        const feedbackDoc = await firebase
          .firestore()
          .collection('familyFeedback')
          .doc(requestId)
          .get();

        if (!feedbackDoc.exists) {
          console.error('Feedback request not found:', requestId);
          commit('setCurrentFeedbackRequest', null);
          return;
        }

        const data = feedbackDoc.data() as FamilyFeedback;
        
        // Fetch resident data to get room and name
        let residentRoom = '';
        let residentName = data.residentName || 'Unknown';
        if (data.residentId) {
          try {
            const residentDoc = await firebase
              .firestore()
              .collection('residents')
              .doc(data.residentId)
              .get();
            
            if (residentDoc.exists) {
              const residentData = residentDoc.data();
              residentRoom = residentData?.roomName || '';
              residentName = residentData?.name || residentName;
            }
          } catch (err) {
            console.error(`Error fetching resident ${data.residentId}:`, err);
          }
        }

        // Fetch family member relationship and photo from familyHubUsers collection
        let familyMemberRelation = '';
        let familyMemberPhotoUrl = '';
        if (data.familyMemberId) {
          try {
            const familyMemberDoc = await firebase
              .firestore()
              .collection('familyHubUsers')
              .doc(data.familyMemberId)
              .get();
            
            if (familyMemberDoc.exists) {
              const familyMemberData = familyMemberDoc.data();
              familyMemberRelation = familyMemberData?.relationship || '';
              familyMemberPhotoUrl = familyMemberData?.thumbnailUrl || familyMemberData?.profilePicUrl || '';
            }
          } catch (err) {
            console.error(`Error fetching family member data:`, err);
          }
        }

        const feedbackRequest: FamilyFeedbackRequest = {
          id: feedbackDoc.id,
          ...data,
          type: normalizeFeedbackType(data.type),
          reference: data.referenceId,
          residentName,
          residentRoom,
          familyMemberRelation,
          familyMemberPhotoUrl
        };

        commit('setCurrentFeedbackRequest', feedbackRequest);
      } catch (error) {
        console.error('Error fetching feedback request:', error);
        commit('setCurrentFeedbackRequest', null);
        throw error;
      } finally {
        commit('setLoadingCurrentRequest', false);
      }
    },

    async addCommentToFeedbackRequest({ commit, state }, { requestId, text, attachments = [] }: { requestId: string; text: string; attachments?: File[] }) {
      try {
        // TODO: Implement message sending when familyFeedbackMessages permissions are fixed
        console.warn('Message sending not yet implemented - waiting for Firestore permissions');
        return null;
      } catch (error) {
        console.error('Error adding comment:', error);
        throw error;
      }
    },

    async updateFeedbackRequestStatus({ commit }, { requestId, referenceId, status, resolution, resolutionNote }: { requestId: string; referenceId?: string; status: string; resolution?: string; resolutionNote?: string }) {
      try {
        const currentUser = this.state.authModule.user;
        
        // Create descriptive status change message for audit log
        const refId = referenceId || requestId;
        let statusChangeMessage = '';
        if (status === 'New' && resolution === 'Ongoing') {
          statusChangeMessage = `Family Feedback: ${refId} Status changed: New`;
        } else if (status === 'Open' && resolution === 'Ongoing') {
          statusChangeMessage = `Family Feedback: ${refId} Status changed: Open - Ongoing`;
        } else if (status === 'Pending' && resolution === 'WaitingOnFamily') {
          statusChangeMessage = `Family Feedback: ${refId} Status changed: Pending - Waiting on family`;
        } else if (status === 'Closed' && resolution === 'Resolved') {
          statusChangeMessage = `Family Feedback: ${refId} Status changed: Closed - Resolved`;
        } else {
          statusChangeMessage = `Family Feedback: ${refId} Status changed: ${status}`;
        }

        const updateData: any = {
          status,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          updatedById: currentUser.id,
          updatedByName: currentUser.name,
          updatedByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [statusChangeMessage],
          initiatorAccessLevel: currentUser.accessLevel,
          initiatorName: currentUser.name
        };

        if (resolution) {
          updateData.resolution = resolution;
        }

        if (status === 'Closed') {
          updateData.dateClosed = firebase.firestore.FieldValue.serverTimestamp();
          if (resolutionNote) {
            updateData.resolutionNote = resolutionNote;
          }
        }

        await firebase
          .firestore()
          .collection('familyFeedback')
          .doc(requestId)
          .update(updateData);

        commit('updateFeedbackRequestStatus', { requestId, status });
        
        return true;
      } catch (error) {
        console.error('Error updating feedback request status:', error);
        throw error;
      }
    },

    async assignStaffToFeedbackRequest({ commit }, { requestId, staffId, staffName }: { requestId: string; staffId: string; staffName: string }) {
      try {
        const currentUser = this.state.authModule.user;
        await firebase
          .firestore()
          .collection('familyFeedback')
          .doc(requestId)
          .update({
            staffAssignedId: staffId,
            staffAssignedName: staffName,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedById: currentUser.id,
            updatedByType: UserTypeEnum.Staff,
            typesOfRecordChanged: [`Family Feedback: Staff Assignment`],
            initiatorAccessLevel: currentUser.accessLevel,
            initiatorName: currentUser.name
          });

        return true;
      } catch (error) {
        console.error('Error assigning staff to feedback request:', error);
        throw error;
      }
    },

    async getFeedbackMessages({ commit }, feedbackId: string) {
      commit('setLoadingMessages', true);
      try {
        const facilityId = this.state.authModule.currentFacility;
        
        const snapshot = await firebase
          .firestore()
          .collection('familyFeedbackMessages')
          .where('feedbackId', '==', feedbackId)
          .where('facilityId', '==', facilityId)
          .orderBy('createdAt', 'asc')
          .get();

        const messages: FeedbackMessage[] = snapshot.docs.map(doc => {
          const data = doc.data() as FeedbackMessage;
          const createdAtDate = (data.createdAt as any)?.toDate ? (data.createdAt as any).toDate() : data.createdAt;
          return {
            id: doc.id,
            ...data,
            createdAtDisplay: moment(createdAtDate).isSame(moment(), 'day')
              ? localizedDate(createdAtDate, this.state.authModule.currentFacilityData.timeFormat)
              : localizedDate(createdAtDate)
          };
        });

        commit('setFeedbackMessages', messages);
        return messages;
      } catch (error) {
        console.error('Error fetching feedback messages:', error);
        throw error;
      } finally {
        commit('setLoadingMessages', false);
      }
    },

    async sendFeedbackMessage({ commit, state }, { feedbackId, text, medias = [] }: { feedbackId: string; text: string; medias?: any[] }) {
      try {
        const currentUser = this.state.authModule.user;
        const currentFeedback = state.currentFeedbackRequest;
        
        if (!currentFeedback) {
          throw new Error('No current feedback request');
        }

        const messageData: any = {
          feedbackId,
          feedbackName: `${currentFeedback.type}: ${currentFeedback.subject}`,
          facilityId: this.state.authModule.currentFacility,
          orgId: this.state.authModule.currentFacilityData.orgId,
          residentId: currentFeedback.residentId,
          senderId: currentUser.id,
          senderName: currentUser.name,
          senderPhotoUrl: currentUser.profilePicUrl || currentUser.thumbnailUrl || '',
          senderRole: 'staff',
          receiverId: null,
          text,
          medias: medias || [],
          read: false,
          readDateTime: null,
          createdAt: firebase.firestore.FieldValue.serverTimestamp()
        };

        const docRef = await firebase
          .firestore()
          .collection('familyFeedbackMessages')
          .add(messageData);

        // Update unread count in feedback document
        await firebase
          .firestore()
          .collection('familyFeedback')
          .doc(feedbackId)
          .update({
            unreadMessagesFromStaff: firebase.firestore.FieldValue.increment(1)
          });

        const newMessage: FeedbackMessage = {
          id: docRef.id,
          ...messageData,
          createdAt: new Date()
        };

        return newMessage;
      } catch (error) {
        console.error('Error sending feedback message:', error);
        throw error;
      }
    },

    subscribeFeedbackMessages({ commit, state }, { feedbackId, onMessageAdded }: { feedbackId: string; onMessageAdded: (message: FeedbackMessage) => void }) {
      const facilityId = this.state.authModule.currentFacility;
      
      const unsubscribe = firebase
        .firestore()
        .collection('familyFeedbackMessages')
        .where('feedbackId', '==', feedbackId)
        .where('facilityId', '==', facilityId)
        .where('createdAt', '>=', new Date())
        .orderBy('createdAt', 'asc')
        .onSnapshot(snapshot => {
          snapshot.docChanges().forEach(change => {
            if (change.type === 'added') {
              const data = change.doc.data() as FeedbackMessage;
              const createdAtDate = (data.createdAt as any)?.toDate ? (data.createdAt as any).toDate() : data.createdAt;
              
              // Check if message already exists in state to prevent duplicates
              const messageExists = state.feedbackMessages.some((m: FeedbackMessage) => m.id === change.doc.id);
              if (messageExists) {
                return;
              }
              
              const message: FeedbackMessage = {
                id: change.doc.id,
                ...data,
                createdAtDisplay: moment(createdAtDate).isSame(moment(), 'day')
                  ? localizedDate(createdAtDate, this.state.authModule.currentFacilityData.timeFormat)
                  : localizedDate(createdAtDate)
              };
              
              commit('addFeedbackMessage', message);
              onMessageAdded(message);
            }
          });
        });

      return unsubscribe;
    },

    async markFeedbackMessagesAsRead({ commit }, feedbackId: string) {
      try {
        const facilityId = this.state.authModule.currentFacility;
        
        const snapshot = await firebase
          .firestore()
          .collection('familyFeedbackMessages')
          .where('feedbackId', '==', feedbackId)
          .where('facilityId', '==', facilityId)
          .where('read', '==', false)
          .where('senderRole', '==', 'family')
          .get();

        const batch = firebase.firestore().batch();
        
        snapshot.docs.forEach(doc => {
          batch.update(doc.ref, {
            read: true,
            readDateTime: firebase.firestore.FieldValue.serverTimestamp()
          });
        });

        await batch.commit();

        // Update unread count in feedback document
        if (snapshot.docs.length > 0) {
          await firebase
            .firestore()
            .collection('familyFeedback')
            .doc(feedbackId)
            .update({
              unreadMessagesFromFamilyMember: 0
            });
        }

        return true;
      } catch (error) {
        console.error('Error marking feedback messages as read:', error);
        throw error;
      }
    }
  }
};

export default feedbackModule;
